import { useSite } from "@/contexts/SiteContext";
import { Quote } from "lucide-react";

/**
 * Banda de statistici — cifre Playfair aurii + testimonial italic + mandală aurie discretă.
 * Editabilă din /admin/sectiuni → stats_band.
 */

function Mandala({ size = 150 }) {
  const petals = Array.from({ length: 12 });
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden="true" style={{ opacity: 0.25 }}>
      <g stroke="var(--ct-gold)" strokeWidth="0.6">
        <circle cx="50" cy="50" r="46" />
        <circle cx="50" cy="50" r="34" />
        <circle cx="50" cy="50" r="20" />
        <circle cx="50" cy="50" r="8" />
        {petals.map((_, i) => (
          <ellipse key={`a${i}`} cx="50" cy="24" rx="7" ry="16" transform={`rotate(${i * 30} 50 50)`} />
        ))}
        {petals.map((_, i) => (
          <ellipse key={`b${i}`} cx="50" cy="36" rx="4" ry="9" transform={`rotate(${i * 30 + 15} 50 50)`} />
        ))}
      </g>
    </svg>
  );
}

export default function StatsBand() {
  const { sectionContent } = useSite();
  const c = sectionContent("stats_band");
  if (!c) return null;
  const items = c.items || [];
  const t = c.testimonial || {};
  if (items.length === 0 && !t.text) return null;

  return (
    <section
      data-testid="stats-band-section"
      className="w-full"
      style={{ background: "var(--ct-bg)", borderTop: "1px solid var(--ct-border-soft)", borderBottom: "1px solid var(--ct-border-soft)" }}
    >
      <div className="max-w-[1440px] mx-auto px-4 lg:px-12 py-10 lg:py-12 flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
        {/* Cifre */}
        <div className="flex items-stretch justify-center lg:justify-start gap-6 sm:gap-10 lg:gap-12 flex-wrap">
          {items.map((it, i) => (
            <div key={i} className="flex items-stretch gap-6 sm:gap-10 lg:gap-12" data-testid={`stat-${i}`}>
              {i > 0 && <span className="w-px self-stretch" style={{ background: "var(--ct-divider)" }} />}
              <div className="text-center lg:text-left">
                <p data-el="item_title" data-field={`items.${i}.value`} className="font-serif leading-none text-4xl sm:text-5xl" style={{ color: "var(--ct-gold-soft)" }}>{it.value}</p>
                <p data-el="item_body" data-field={`items.${i}.label`} className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] mt-2" style={{ color: "var(--ct-text-muted)" }}>{it.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial + mandală */}
        {t.text && (
          <div className="flex items-center gap-6 lg:gap-10 lg:ml-auto max-w-xl">
            <div className="flex items-start gap-3">
              <Quote size={20} className="shrink-0 mt-1" style={{ color: "var(--ct-gold)" }} />
              <div>
                <p data-el="testimonial" data-field="testimonial.text" className="font-serif italic text-sm sm:text-base leading-relaxed" style={{ color: "var(--ct-text-muted)" }}>
                  {t.text}
                </p>
                {t.author && <p className="text-xs mt-2 font-semibold" style={{ color: "var(--ct-text)" }}>— <span data-field="testimonial.author">{t.author}</span></p>}
              </div>
            </div>
            <div className="hidden xl:block shrink-0">
              <Mandala />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
